import { dateBeforeA } from '@src/utils/helpers';
import { QUESTION_TYPE } from './ddf.validation';
import { getCompletedTableRows } from '@src/app-components/table-input/jpaTableInput.helpers';

const JPA_INFO_START_INDEX = 8; // it needs to be updated if the index of the first jpa info form changes

const onNumberKeyDown = (e) => {
  // do not allow exponent notation in number inputs
  if (e.key === 'e' || e.key === 'E') {
    e.preventDefault();
  }
};

export const getResponseForHideKey = (value) => {
  if (value === true) return 'YES';
  if (value === false) return 'NO';
  return String(value);
};

export const shouldHideForResponse = (value, responseForHide) => {
  if (!responseForHide || typeof responseForHide !== 'object') {
    return null;
  } // Keep the related question step hidden until a valid selection is made
  if (value === undefined || value === null || value === '') {
    return value;
  }
  const responseKey = getResponseForHideKey(value);
  return responseForHide[responseKey] ? value : null;
};

export const manageSectionVisibilityHelper = ({
  calculateQuestionFormIndex,
  clearAnswers,
  getSectionParentId,
  isInformationHidden = false,
  jpaFormID,
  sections,
  targetValue,
  updateSection,
  value,
}) => {
  const questionFormIndex = calculateQuestionFormIndex();
  const isHidden = isInformationHidden || value === targetValue;

  const childUpdate = {
    hidden: isHidden,
    ...(isHidden && {
      valid: true,
      touched: false,
      dirty: false,
    }),
  };

  if (sections[questionFormIndex]?.hidden !== isHidden) {
    updateSection(questionFormIndex, childUpdate);
    clearAnswers(jpaFormID);
  }

  const parentId = getSectionParentId(questionFormIndex);
  if (!parentId) return;

  const childSections = sections
    .map((section, index) => (index === questionFormIndex ? { ...section, ...childUpdate } : section))
    .filter((section) => section.parentId === parentId);

  const areAllSubStepsHidden = childSections.every((section) => section.hidden);

  const areVisibleSubStepsValid = childSections
    .filter((section) => !section.hidden)
    .every((section) => section.valid !== false);

  updateSection(parentId, {
    hidden: areAllSubStepsHidden,
    valid: areVisibleSubStepsValid,
  });
};

export const calculateQuestionFormIndex = (sections, jpaBasicInfo, stepNo) => {
  // Calculate the corresponding form question section to the current JPA info section.
  const firstJPAFormInfoIndex = JPA_INFO_START_INDEX;
  const formQuestionStartIndex = sections.length - jpaBasicInfo.length - 3;
  const currentStepIndex = stepNo;
  const stepDifference = currentStepIndex - firstJPAFormInfoIndex; // stepDifference will be a negative number or 0
  const formQuestionIndex = formQuestionStartIndex + stepDifference;

  return formQuestionIndex;
};

const comparisonFunctions = {
  contains: (inputValue, triggerValue) =>
    Array.isArray(inputValue)
      ? inputValue.some((item) =>
          String(item?.value ?? item)
            .toLowerCase()
            .includes(String(triggerValue).toLowerCase())
        )
      : String(inputValue).toLowerCase().includes(String(triggerValue).toLowerCase()),
  exists: (inputValue) =>
    inputValue !== undefined && inputValue !== null && inputValue !== '' && inputValue?.length > 0,
  not: (inputValue, triggerValue) => {
    if (inputValue === undefined || inputValue === null || inputValue === '') {
      return false;
    }
    return String(inputValue).trim().toLowerCase() !== String(triggerValue).trim().toLowerCase();
  },
  'row exists': (inputValue) => inputValue.length > 0,
  '=': (inputValue, triggerValue) => String(inputValue).toLowerCase() === String(triggerValue).toLowerCase(),
};

const yesNoAnswerMap = {
  true: 'YES',
  false: 'NO',
};

const getComparisonValue = ({ formId, questionId, typeId, value }) => {
  if (typeId === QUESTION_TYPE.yesNo) {
    return yesNoAnswerMap[value];
  }
  if (typeId === QUESTION_TYPE.multiselect) {
    return value?.map((item) => item.value);
  }
  if (typeId === QUESTION_TYPE.table) {
    return getCompletedTableRows(formId, questionId);
  }

  return value;
};

export const getQuestionVisibility = ({ formId, formValues, parentQuestionTree }) => {
  if (!parentQuestionTree || !parentQuestionTree.length) {
    return true;
  }

  const areAllParentsVisible = parentQuestionTree.every((parent) => {
    const parentKey = `${parent.parentTypeID}-${parent.parentID}`;
    const parentValue = formValues[parentKey];
    const comparisonFunction = comparisonFunctions?.[parent.comparison?.trim()?.toLowerCase()];
    const comparisonValue = getComparisonValue({
      formId,
      questionId: parent.parentID,
      typeId: parent.parentTypeID,
      value: parentValue,
    });
    const isParentVisible = comparisonFunction ? comparisonFunction(comparisonValue, parent.triggerValue) : false;

    return isParentVisible;
  });

  return areAllParentsVisible;
};

export function getOtherProps({
  allowsMultiselect,
  componentId,
  handleOnChange,
  isBasicContactInput,
  isContactInput,
  isDateInput,
  isFileInput,
  isNumberInput,
  isOptionInput,
  isReadOnly,
  isShortAnswerInput,
  isTableInput,
  jpaContactAnswerSet,
  jpaFormID,
  jpaLists,
  jpaQuestionID,
  onChange,
  onChangeProps,
  questionData,
}) {
  const props = {};

  if (isOptionInput) {
    props.options = jpaLists?.map((answer) => ({
      label: answer.valueDesc,
      sortOrder: answer.sortOrder,
      value: answer.valueDesc,
    }));
    props.fullWidth = false;
  }

  if (isNumberInput) {
    props.type = 'number';
    props.onKeyDown = onNumberKeyDown;
  }

  if (isShortAnswerInput) {
    props.maxLength = 500;
  }

  if (isDateInput) {
    props.min = dateBeforeA;
  }

  if (allowsMultiselect) {
    props.fullWidth = false;
    props.handleChange = handleOnChange;
    props.multi = true;
  }

  if (isTableInput) {
    props.formId = jpaFormID;
    props.isReadOnly = isReadOnly;
    props.questionId = jpaQuestionID;
  }

  if (isFileInput) {
    props.componentId = componentId;
    props.formId = jpaFormID;
    props.onChange = onChange;
    props.onChangeProps = onChangeProps;
    props.questionData = questionData;
  }

  if (isContactInput) {
    props.contactType = questionData.contactType;
    props.dataKey = 'jpaContactAnswers';
    props.formId = jpaFormID;
    props.initialContacts = jpaContactAnswerSet.filter((contact) => contact.questionID === jpaQuestionID);
    props.isReadOnly = isReadOnly;
    props.questionId = jpaQuestionID;
    props.questionType = QUESTION_TYPE.contact;
  }

  if (isBasicContactInput) {
    props.contactType = questionData.contactType;
    props.dataKey = 'jpaContactAnswers';
    props.formId = jpaFormID;
    props.initialContacts = jpaContactAnswerSet.filter((contact) => contact.questionID === jpaQuestionID);
    props.isReadOnly = isReadOnly;
    props.questionId = jpaQuestionID;
    props.questionType = QUESTION_TYPE.basicContact;
  }

  return props;
}
